import { createSelector } from 'reselect';
import { ArmyOrganisationView } from '@Selectors/builder/myArmyList/make/ArmyOrganisationView';
import { selectedElementsPresenter } from '@Selectors/builder/myArmyList/make/validation/selected-elements.presenter';
import {
  ArmyOrganisationGroup,
  ConstrainedItem,
  ConstraintField,
  ConstraintItemType,
  ConstraintType,
  SelectedElement,
} from '@Models';
import { getArmyOrganisationGroups } from '@Selectors/builder/myArmyList/make/unit/edit/selector/editSelector';
import { constrainedOjectsPresenter } from '@Selectors/builder/myArmyList/make/validation/constrained-objects.presenter';
import { getArmyList } from '@Selectors/builder/myArmyList/export/exportSelector';

export const buildArmyOrganisationView = (
  elem: ConstrainedItem,
  organisationGroup: ArmyOrganisationGroup | null,
  seletedElement: SelectedElement | null,
  max: number,
): ArmyOrganisationView[] => {
  const constraints = organisationGroup?.constraints ?? [];

  if (constraints.length === 0) {
    return [
      {
        id: elem.id,
        name: elem.name,
        logo: organisationGroup?.logo ?? '',
        good: true,
        rate: `${Math.round(((seletedElement?.points ?? 0) * 100) / max)} %`,
        total: `${seletedElement?.points ?? 0} pts`,
        longTotal: `${seletedElement?.points ?? 0} pts`,
        current: `${seletedElement?.points ?? 0} pts`,
      } satisfies ArmyOrganisationView as ArmyOrganisationView,
    ];
  }

  return constraints.map((constraint) => {
    let rate = `${Math.round(((seletedElement?.points ?? 0) * 100) / max)}`;
    let total = `${seletedElement?.points ?? 0}`;
    let longTotal = `${seletedElement?.points ?? 0}`;
    let current = `${seletedElement?.points ?? 0}`;
    let good = true;

    if (constraint.field === ConstraintField.Points) {
      if (constraint.percentValue) {
        const value = Math.round((constraint.value * max) / 100);

        rate += ` / ${constraint.value}% ${ConstraintType[constraint.type]}`;
        total += ` / ${value} pts`;
        longTotal += ` / ${value} pts`;
      } else {
        rate += ' %';
        total += ` / ${constraint.value} pts`;
        longTotal += ` / ${constraint.value}  pts`;
      }

      current += ` pts`;

      const delta =
        (seletedElement?.points ?? 0) -
        (constraint.percentValue
          ? Math.round((constraint.value * max) / 100)
          : constraint.value);
      switch (constraint.type) {
        case ConstraintType.Max:
          if (delta < 0) {
            longTotal += ` - available ${Math.abs(delta)} pts`;
          } else {
            longTotal += ` - excess ${Math.abs(delta)} pts`;
            good = false;
          }
          break;
        case ConstraintType.Min:
          if (delta < 0) {
            longTotal += ` - required ${Math.abs(delta)} pts`;
            good = false;
          }
          break;
      }
    } else if (constraint.field === ConstraintField.Selections) {
      rate += ' %';
      current = `${seletedElement?.count ?? 0}`;
      total = `${seletedElement?.count ?? 0} / ${constraint.value}`;
      longTotal = `${seletedElement?.count ?? 0} / ${constraint.value}`;

      const delta = (seletedElement?.count ?? 0) - constraint.value;
      switch (constraint.type) {
        case ConstraintType.Max:
          if (delta < 0) {
            longTotal += ` - available ${Math.abs(delta)}`;
          } else {
            longTotal += ` - excess ${Math.abs(delta)}`;
            good = false;
          }
          break;
        case ConstraintType.Min:
          if (delta < 0) {
            longTotal += ` - required ${Math.abs(delta)}`;
            good = false;
          }
          break;
      }
    }

    return {
      id: elem.id,
      name: elem.name,
      logo: organisationGroup?.logo ?? '',
      good: good,
      rate: rate,
      total: total,
      longTotal: longTotal,
      current: current,
    } satisfies ArmyOrganisationView as ArmyOrganisationView;
  });
};

export const armyOrganisationPresenter = createSelector(
  [
    constrainedOjectsPresenter,
    selectedElementsPresenter,
    getArmyOrganisationGroups,
    getArmyList,
  ],
  (
    constrainedOjects,
    seletedElements,
    armyOrganisationGroups,
    armyList,
  ): ArmyOrganisationView[] => {
    return constrainedOjects
      .filter((elem) => elem.type === ConstraintItemType.Organisation)
      .filter(
        (elem) =>
          !armyOrganisationGroups.find(
            (group) => group.organisationId === elem.id,
          )?.hidden,
      )
      .flatMap((elem) => {
        const armyOrganisationGroup =
          armyOrganisationGroups.find(
            (group) => group.organisationId === elem.id,
          ) ?? null;
        const seletedElement =
          seletedElements.find(
            (selected) =>
              selected.constrained.id === elem.id &&
              selected.constrained.type === elem.type,
          ) ?? null;

        return buildArmyOrganisationView(
          elem,
          armyOrganisationGroup ?? null,
          seletedElement,
          armyList?.max ?? 2000,
        );
      });
  },
);
